// TUTORIAL / FIRST-RUN GUIDE — the screens (logic + text: src/app/tutorial.ts). Self-contained DOM:
//  - hint card: a small timed card over the run (never blocks combat; tap / ✕ / the Back button dismisses it); the level-up
//    hint docks inside the level-up panel instead (that screen is already paused)
//  - lesson coach + "move here" marker for the Basics lesson
//  - HOW TO PLAY: tabbed reference pages (LB / RB or [ ]), from the title and the pause menu
//  - the first-launch offer ("New here? Play the Basics")
// Every button prompt is device-matched: keyboard / controller / touch spans (body.pad / body.touch pick one), with data-bind /
// data-bindk / data-g attributes so main.ts's glyph pass relabels them when the controller or bindings change.
import { GLYPHS, type PadFamily, type UiAction } from './input';
import type { ActionId, Device } from './bindings';
import { HEAL_ICON, fusionIcon, techIcon, traitIcon } from './icons';
import { FUSIONS, type FusionId } from '../core/fusions';
import type { Sim } from '../core/sim';
import {
  CONTROL_ROWS, LESSON_STEPS, LESSON_TEXT, TIPS, howtoPages, type HowtoPage, type IconRef, type Lesson, type TipId,
} from './tutorial';

export interface TutorialHost {
  /** first bound key / pad button of an action, as a label ('' if none) */
  glyph(action: ActionId, dev: Device): string;
  /** controller family for fixed buttons (Start, sticks) */
  family(): PadFamily;
  /** [previous, next] tab prompts for the current device */
  tabLabels(): [string, string];
  click(): void;
  /** HOW TO PLAY › Basics › PLAY THE LESSON */
  startLesson(): void;
  /** HOW TO PLAY closed (BACK) */
  closeHowto(): void;
  /** SKIP on the lesson coach */
  skipLesson(): void;
  /** the first-launch offer was answered */
  offerAnswered(play: boolean): void;
}

const $ = <T extends HTMLElement>(s: string) => document.querySelector(s) as T;
const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);

/** small original glyphs for things icons.ts has no picture of (chest, cache, elite, boss, wall, …) */
const S = 'fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"';
const F = 'fill="currentColor" stroke="none"';
const GLYPH_SVG: Record<string, [string, string]> = {
  chest: ['#ffcc4a', `<path ${F} opacity=".35" d="M4 11h16v8H4z"/><path ${S} d="M4 11h16v8H4zM4 11V8a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3v3M10.5 11v3h3v-3"/>`],
  cache: ['#c9a2ff', `<path ${F} opacity=".35" d="M12 3l7.8 4.5v9L12 21l-7.8-4.5v-9z"/><path ${S} d="M12 3l7.8 4.5v9L12 21l-7.8-4.5v-9zM12 8v8M8.5 10l7 4M15.5 10l-7 4"/>`],
  elite: ['#ffb53a', `<path ${F} d="M12 2.5l2.4 5 5.4.6-4 3.7 1.1 5.3L12 14.4l-4.9 2.7 1.1-5.3-4-3.7 5.4-.6z"/><path ${S} opacity=".6" d="M5 21h14"/>`],
  boss: ['#ff5a7a', `<path ${S} d="M4 9l3 3 5-7 5 7 3-3-1.5 10h-13z"/><circle ${F} cx="12" cy="14.5" r="1.8"/><path ${S} opacity=".55" d="M2 22h20"/>`],
  wall: ['#9fe0ff', `<path ${S} d="M3 6h18v13H3zM3 10.5h18M3 15h18M8 6v4.5M15 6v4.5M11.5 10.5V15M5 15v4M18 15v4"/>`],
  mote: ['#7ff0ff', `<path ${F} d="M12 3l5 9-5 9-5-9z"/><path ${S} opacity=".55" d="M3 12h2M19 12h2M12 1v1M12 22v1"/>`],
  shard: ['#ffe08a', `<path ${F} d="M12 2l6 7-6 13-6-13z"/><path stroke="#2a1400" stroke-width="1.4" d="M6 9h12M12 2v20" opacity=".45"/>`],
  book: ['#ffd45e', `<path ${S} d="M4 5.5C7 4 10 4.5 12 6c2-1.5 5-2 8-.5V19c-3-1.5-6-1-8 .5-2-1.5-5-2-8-.5zM12 6v13.5"/>`],
  move: ['#9fe0ff', `<circle ${S} cx="12" cy="12" r="8.5"/><circle ${F} cx="15" cy="9" r="3.2"/><path ${S} opacity=".6" d="M12 12l3-3"/>`],
  aim: ['#ff8f9f', `<circle ${S} cx="12" cy="12" r="7"/><circle ${F} cx="12" cy="12" r="1.8"/><path ${S} d="M12 2v4M12 18v4M2 12h4M18 12h4"/>`],
  mods: ['#ff9f6b', `<path ${S} d="M5 4v16M12 4v16M19 4v16"/><circle ${F} cx="5" cy="15" r="2.4"/><circle ${F} cx="12" cy="8" r="2.4"/><circle ${F} cx="19" cy="13" r="2.4"/>`],
  item: ['#5affd8', `<path ${S} d="M4 12a8 8 0 0 1 14-5.3M20 12a8 8 0 0 1-14 5.3"/><path ${S} d="M18 3v4h-4M6 21v-4h4"/>`],
};
export function iconHtml(r: IconRef): string {
  switch (r.t) {
    case 'tech': return techIcon(r.id, !!r.evo);
    case 'trait': return traitIcon(r.id);
    case 'fusion': return fusionIcon(r.id ?? (Object.keys(FUSIONS) as FusionId[])[0]);
    case 'heal': return HEAL_ICON;
    case 'glyph': { const [c, b] = GLYPH_SVG[r.id] ?? GLYPH_SVG.mote; return `<svg class="ico tut" viewBox="0 0 24 24" style="color:${c}" aria-hidden="true">${b}</svg>`; }
  }
}

export class TutorialUi {
  private card: HTMLElement;
  private coachEl: HTMLElement;
  private markEl: HTMLElement;
  private queue: TipId[] = [];
  private shown: TipId | null = null;
  private left = 0; private gap = 0;
  private coachKey = '';
  /** HOW TO PLAY */
  tab = 0;
  private pages: HowtoPage[] = howtoPages();
  /** HOW TO PLAY opened from the pause menu (no lesson button there: it would end the run) */
  fromRun = false;
  /** first-launch offer: open + focused button (0 = play, 1 = not now) */
  offerOpen = false; private offerSel = 0;

  constructor(private host: TutorialHost) {
    const hud = $('#hud');
    this.card = document.createElement('div'); this.card.id = 'tipcard'; this.card.className = 'tut'; this.card.setAttribute('role', 'status'); this.card.hidden = true;
    this.card.addEventListener('click', () => this.dismiss());
    this.coachEl = document.createElement('div'); this.coachEl.id = 'tut-coach'; this.coachEl.className = 'tut'; this.coachEl.hidden = true;
    this.coachEl.addEventListener('click', (e) => { if ((e.target as HTMLElement).closest('.tc-skip')) this.host.skipLesson(); });
    this.markEl = document.createElement('div'); this.markEl.id = 'tut-mark'; this.markEl.hidden = true; this.markEl.innerHTML = '<i></i><b>HERE</b>';
    hud.append(this.markEl, this.coachEl, this.card);
    $('#ht-tabs').addEventListener('click', (e) => {
      const el = e.target as HTMLElement, t = el.closest('.stab') as HTMLElement | null, b = el.closest('.bump') as HTMLElement | null;
      if (t) this.setTab(Number(t.dataset.t)); else if (b) this.setTab(this.tab + Number(b.dataset.d));
    });
    $('#ht-body').addEventListener('click', (e) => { if ((e.target as HTMLElement).closest('#ht-lesson')) this.host.startLesson(); });
    $('#ht-back').addEventListener('click', () => this.host.closeHowto());
    this.initHowtoTouch();
    $('#tut-offer').addEventListener('click', (e) => {
      const b = (e.target as HTMLElement).closest('.btn') as HTMLElement | null; if (!b) return;
      this.answerOffer(b.id === 'to-play');
    });
  }

  // ---------------------------------------------------------------- device-matched prompts
  private K(a: ActionId): string { return `<kbd data-bindk="${a}">${esc(this.host.glyph(a, 'key') || '—')}</kbd>`; }
  private P(a: ActionId): string { return `<kbd data-bind="${a}">${esc(this.host.glyph(a, 'pad') || '—')}</kbd>`; }
  private G(g: keyof (typeof GLYPHS)['xbox']): string { return `<kbd data-g="${g}">${esc(GLYPHS[this.host.family()][g])}</kbd>`; }
  private tri(kb: string, gp: string, tc: string): string { return `<span class="dv kb">${kb}</span><span class="dv gp">${gp}</span><span class="dv tc">${tc}</span>`; }
  /** keyboard / controller halves of a token (the CONTROLS table shows both) */
  private part(tok: string, dev: 'k' | 'p'): string {
    const k = dev === 'k';
    switch (tok) {
      case 'move': return k ? `${this.K('moveUp')}${this.K('moveLeft')}${this.K('moveDown')}${this.K('moveRight')} / arrows` : 'Left stick';
      case 'aimstick': return k ? 'Mouse' : 'Right stick';
      case 'pause': return k ? `${this.K('pause')} / <kbd>Esc</kbd>` : this.G('start');
      case 'confirm': return k ? '<kbd>Enter</kbd>' : this.P('confirm');
      case 'picks': return k ? `${this.K('pick1')} ${this.K('pick2')} ${this.K('pick3')}` : this.P('confirm');
      case 'tabs': return k ? `${this.K('tabPrev')} ${this.K('tabNext')}` : `${this.P('tabPrev')} ${this.P('tabNext')}`;
      default: return k ? this.K(tok as ActionId) : this.P(tok as ActionId);
    }
  }
  /** text with {tokens} → HTML with device-matched prompts */
  fmt(text: string, n = 0): string {
    return esc(text).replace(/\{(k:|p:)?([a-zA-Z0-9]+)\}/g, (_m, dev: string | undefined, tok: string) => {
      if (tok === 'n') return String(n);
      if (dev) return this.part(tok, dev === 'k:' ? 'k' : 'p');
      const touch: Record<string, string> = {
        move: 'a thumb dragged anywhere on the screen', surge: 'the <b>SURGE</b> button', confirm: 'a tap', picks: 'a tap on the card', aimstick: 'auto aim (touch)',
        aimmode: 'Settings', reroll: 'the REROLL button', ban: 'the BAN button', pause: 'the pause button', tabs: 'a tap on a tab',
      };
      const tc = touch[tok] ?? tok;
      if (tok === 'aimmode') return this.tri(this.K('aimMode'), this.P('aimMode'), tc);
      return this.tri(this.part(tok, 'k'), this.part(tok, 'p'), tc);
    });
  }

  // ---------------------------------------------------------------- hint cards
  /** queue first-time hints (the level-up one docks in the level-up panel) */
  showTips(ids: TipId[], sim: Sim | null): void {
    for (const id of ids) {
      if (id === 'levelup') this.dockLevelup(TIPS.levelup.title, this.levelupBody(sim), TIPS.levelup.icon);
      else if (!this.queue.includes(id) && this.shown !== id) this.queue.push(id);
    }
  }
  private levelupBody(sim: Sim | null): string {
    let t = this.fmt(TIPS.levelup.body);
    if (sim && (sim.rerolls > 0 || sim.bans > 0)) t += ` <em>You have ${sim.rerolls} reroll${sim.rerolls === 1 ? '' : 's'} and ${sim.bans} ban${sim.bans === 1 ? '' : 's'} this run.</em>`;
    return t;
  }
  /** a hint inside the level-up panel (stays until the draft closes) */
  dockLevelup(title: string, bodyHtml: string, icon: IconRef): void {
    const el = $('#lu-tip'); if (!el) return;
    el.innerHTML = `<div class="tc-ic">${iconHtml(icon)}</div><div class="tc-tx"><h4>${esc(title)}</h4><p>${bodyHtml}</p></div>`;
    el.hidden = false;
  }
  undockLevelup(): void { const el = document.getElementById('lu-tip'); if (el) { el.hidden = true; el.innerHTML = ''; } }
  /** the card on screen (or null) */
  get current(): TipId | null { return this.shown; }
  dismiss(): void {
    if (!this.shown) return;
    this.shown = null; this.card.classList.remove('show'); this.gap = 0.8;
    setTimeout(() => { if (!this.shown) this.card.hidden = true; }, 260);
  }
  /** clear everything (run ended / title) */
  reset(): void { this.queue.length = 0; this.shown = null; this.card.classList.remove('show'); this.card.hidden = true; this.undockLevelup(); this.coach(null, null); }
  /** per frame; `live` = the run is in play (the card's timer only runs then, so a pause never eats it) */
  tick(dt: number, live: boolean): void {
    if (!live) return;
    if (this.shown) { this.left -= dt; if (this.left <= 0) this.dismiss(); return; }
    if (this.gap > 0) { this.gap -= dt; return; }
    const id = this.queue.shift(); if (!id) return;
    const t = TIPS[id], body = this.fmt(t.body);
    this.left = Math.min(13, 6 + t.body.split(' ').length * 0.12);
    this.card.innerHTML = `<div class="tc-ic">${iconHtml(t.icon)}</div><div class="tc-tx"><h4>${esc(t.title)}</h4><p>${body}</p></div>`
      + `<button class="tc-x" aria-label="Dismiss tip">✕<span class="dv gp"> ${this.G('east')}</span></button><u class="tc-time" style="animation-duration:${this.left.toFixed(1)}s"></u>`;
    this.card.hidden = false; this.shown = id;
    requestAnimationFrame(() => this.card.classList.add('show'));
  }

  // ---------------------------------------------------------------- lesson coach + marker
  /** per frame during the lesson (null hides it). `project` maps a ground point to screen px. */
  coach(lesson: Lesson | null, sim: Sim | null, project?: (x: number, z: number) => { x: number; y: number; vis: boolean }): void {
    if (!lesson || !sim) { this.coachEl.hidden = true; this.markEl.hidden = true; this.coachKey = ''; return; }
    const s = lesson.s, step = lesson.step, i = Math.min(s.index, LESSON_STEPS.length - 1);
    const pass = s.passT > 0 || lesson.done;
    const key = `${s.index}|${pass}|${s.count}|${s.ready}|${document.body.className.includes('pad')}|${document.body.className.includes('touch')}`;
    if (key !== this.coachKey) {
      this.coachKey = key;
      const dots = LESSON_STEPS.map((_, k) => `<i class="${k < s.index || (k === s.index && pass) ? 'ok' : k === s.index ? 'on' : ''}"></i>`).join('');
      let title: string, body: string;
      if (lesson.done) { title = 'Lesson complete!'; body = 'You know the basics. Find every system explained under HOW TO PLAY. Back to the title…'; }
      else {
        const T = LESSON_TEXT[step ?? 'move'];
        title = T.title; body = this.fmt(T.body, s.goal);
        if (step === 'surge' && s.ready) body = this.fmt('The bar is full. Press {surge} now to transform!');
        if (s.goal > 1 && !pass) body += ` <b class="tc-n">${s.count} / ${s.goal}</b>`;
      }
      this.coachEl.innerHTML = `<div class="co-head"><span class="co-step">BASICS ${i + 1}/${LESSON_STEPS.length}</span><span class="co-dots">${dots}</span>`
        + `${lesson.done ? '' : '<button class="tc-skip">SKIP</button>'}</div><h4>${pass && !lesson.done ? '✓ ' : ''}${esc(title)}</h4><p>${body}</p>`;
      this.coachEl.classList.toggle('pass', pass); this.coachEl.classList.toggle('ready', step === 'surge' && s.ready);
    }
    this.coachEl.hidden = sim.state === 'levelup';
    // the card text docks in the level-up panel (the coach is under that screen)
    if (sim.state === 'levelup' && !lesson.done) this.dockLevelup(LESSON_TEXT.card.title, this.fmt(LESSON_TEXT.card.body), { t: 'tech', id: 'nova' });
    // "move here" marker
    const tg = step === 'move' && !pass ? s.target : null;
    if (tg && project) {
      const q = project(tg.x, tg.z), m = 28, w = innerWidth, h = innerHeight;
      const off = !q.vis || q.x < m || q.x > w - m || q.y < m || q.y > h - m;
      const x = Math.max(m, Math.min(w - m, q.x)), y = Math.max(m, Math.min(h - m, q.y));
      this.markEl.style.transform = `translate(${x}px, ${y}px)`; this.markEl.classList.toggle('edge', off); this.markEl.hidden = false;
    } else this.markEl.hidden = true;
  }

  // ---------------------------------------------------------------- HOW TO PLAY
  open(fromRun: boolean): void { this.fromRun = fromRun; this.pages = howtoPages(); this.render(); }
  setTab(i: number): void { this.tab = (i + this.pages.length) % this.pages.length; this.host.click(); this.render(); }
  /** menu input on the HOW TO PLAY screen */
  handle(a: UiAction): void {
    if (a === 'back' || a === 'pause') { this.host.closeHowto(); return; }
    if (a === 'tabPrev' || a === 'left') { this.setTab(this.tab - 1); return; }
    if (a === 'tabNext' || a === 'right') { this.setTab(this.tab + 1); return; }
    if (a === 'down' || a === 'up') { const sc = $('#howto'); sc.scrollBy({ top: (a === 'down' ? 1 : -1) * Math.max(60, sc.clientHeight * 0.4), behavior: 'smooth' }); return; }
    if (a === 'confirm') { if (this.pages[this.tab].lesson && !this.fromRun) this.host.startLesson(); else this.host.closeHowto(); }
  }
  render(): void {
    const [bl, br] = this.host.tabLabels(), pg = this.pages[this.tab];
    $('#ht-tabs').innerHTML = `<b class="bump" data-d="-1">◀ ${esc(bl)}</b>` + this.pages.map((p, k) => `<span class="stab${k === this.tab ? ' on' : ''}" data-t="${k}">${p.tab}</span>`).join('') + `<b class="bump" data-d="1">${esc(br)} ▶</b>`;
    let html = `<h3 class="ht-title">${esc(pg.title)}</h3>`;
    if (pg.lesson && !this.fromRun) html += `<button class="btn primary focus" id="ht-lesson">▶ PLAY THE BASICS LESSON <small>about 2 min · <span class="dv kb"><kbd>Enter</kbd></span><span class="dv gp">${this.P('confirm')}</span></small></button>`;
    for (const sec of pg.sections) html += `<section class="ht-sec"><div class="tc-ic">${iconHtml(sec.icon)}</div><div><h4>${esc(sec.title)}</h4>${sec.body.map((b) => `<p>${this.fmt(b)}</p>`).join('')}</div></section>`;
    if (pg.controls) html += this.controlsTable();
    $('#ht-body').innerHTML = html; $('#howto').scrollTop = 0;
    $('#ht-count').textContent = `${this.tab + 1} / ${this.pages.length}`;
    requestAnimationFrame(() => this.cues());
  }

  // ---------------------------------------------------------------- HOW TO PLAY: touch (owner 2026-11-03)
  // The whole #howto overlay is the native scroll container (CSS), so vertical drags anywhere scroll with momentum. Here:
  // horizontal swipes change pages, a drag never counts as a tap, and the "more content" cues follow the scroll.
  private cuesEl!: HTMLElement;
  private initHowtoTouch(): void {
    const ov = $('#howto'), body = $('#ht-body');
    const cues = this.cuesEl = document.createElement('div');
    cues.id = 'ht-cues'; cues.setAttribute('aria-hidden', 'true'); cues.hidden = true;
    cues.innerHTML = '<i class="f-top"></i><i class="f-bot"><b>MORE ▼</b></i><u class="rail"><i></i></u>';
    document.body.appendChild(cues); // on <body>: a fixed child of the (backdrop-filtered) overlay would scroll with it
    const sync = () => { cues.hidden = !ov.classList.contains('show'); if (!cues.hidden) requestAnimationFrame(() => this.cues()); };
    new MutationObserver(sync).observe(ov, { attributes: true, attributeFilter: ['class'] });
    sync();
    let lastScrollT = 0;
    ov.addEventListener('scroll', () => { lastScrollT = performance.now(); if (g.down) g.moved = true; this.cues(); }, { passive: true });
    addEventListener('resize', () => { if (!cues.hidden) this.cues(); });
    // one gesture at a time (the first finger); `moved` = it was a drag or a scroll, so the click it ends with is swallowed
    const g = { id: -1, x: 0, y: 0, t: 0, down: false, moved: false, flinging: false, horiz: false, noSwipe: false, upT: 0 };
    const reset = () => { body.classList.remove('swiping'); body.style.transform = ''; body.style.opacity = ''; };
    ov.addEventListener('pointerdown', (e) => {
      if (g.down && e.pointerId !== g.id) return;
      const tg = e.target as HTMLElement, ctl = tg.closest('.ht-ctl') as HTMLElement | null;
      Object.assign(g, { id: e.pointerId, x: e.clientX, y: e.clientY, t: performance.now(), down: true, moved: false, horiz: false,
        // a tap that stops a momentum scroll is not a click; a table that scrolls sideways keeps its own horizontal drags
        flinging: performance.now() - lastScrollT < 120, noSwipe: e.pointerType === 'mouse' || (!!ctl && ctl.scrollWidth > ctl.clientWidth + 2) });
    });
    ov.addEventListener('pointermove', (e) => {
      if (!g.down || e.pointerId !== g.id) return;
      const dx = e.clientX - g.x, dy = e.clientY - g.y;
      if (!g.moved && Math.hypot(dx, dy) > 10) g.moved = true;
      if (g.noSwipe) return;
      // horizontal intent: clearly sideways (1.6× more x than y) and past 16 px; vertical drags never reach this (the
      // browser takes them for scrolling and sends pointercancel)
      if (!g.horiz && Math.abs(dx) > 16 && Math.abs(dx) > Math.abs(dy) * 1.6) { g.horiz = true; body.classList.add('swiping'); }
      if (g.horiz) { body.style.transform = `translateX(${dx * 0.35}px)`; body.style.opacity = String(Math.max(0.45, 1 - Math.abs(dx) / 600)); }
    });
    const end = (e: PointerEvent, cancel: boolean) => {
      if (!g.down || e.pointerId !== g.id) return;
      g.down = false; g.upT = performance.now();
      const dx = e.clientX - g.x, dy = e.clientY - g.y, w = ov.clientWidth;
      const flip = !cancel && g.horiz && Math.abs(dx) >= Math.min(70, w * 0.12) && Math.abs(dx) > Math.abs(dy) * 1.6;
      reset();
      if (flip) this.swipeTo(dx < 0 ? 1 : -1);
    };
    ov.addEventListener('pointerup', (e) => end(e, false));
    ov.addEventListener('pointercancel', (e) => end(e, true));
    // a drag / scroll / swipe that ends on a tab, the lesson button or BACK must never press it
    ov.addEventListener('click', (e) => {
      if (e.detail > 0 && (g.moved || g.flinging) && performance.now() - g.upT < 700) { e.stopPropagation(); e.preventDefault(); }
    }, true);
  }
  /** swipe → neighbouring page, sliding in from the swipe side */
  private swipeTo(d: number): void {
    this.setTab(this.tab + d);
    const body = $('#ht-body');
    body.classList.add('swiping'); body.style.transform = `translateX(${d * 40}px)`; body.style.opacity = '0.3';
    requestAnimationFrame(() => requestAnimationFrame(() => {
      body.classList.remove('swiping'); body.style.transition = 'transform .18s ease-out, opacity .18s'; body.style.transform = ''; body.style.opacity = '';
      setTimeout(() => { body.style.transition = ''; }, 220);
    }));
  }
  /** fades at the edges with more to scroll, and the touch scroll rail */
  private cues(): void {
    const c = this.cuesEl; if (!c || c.hidden) return;
    const ov = $('#howto'), tabs = $('#ht-tabs');
    const max = ov.scrollHeight - ov.clientHeight, top = ov.scrollTop;
    const tb = Math.max(0, tabs.getBoundingClientRect().bottom);
    c.classList.toggle('scroll', max > 2); c.classList.toggle('up', top > 4); c.classList.toggle('down', max - top > 4);
    const ft = c.querySelector('.f-top') as HTMLElement; ft.style.top = `${tb}px`;
    const rail = c.querySelector('.rail') as HTMLElement, th = rail.firstElementChild as HTMLElement;
    const r0 = tb + 4, rh = Math.max(20, ov.clientHeight - r0 - 8);
    rail.style.top = `${r0}px`; rail.style.height = `${rh}px`;
    if (max > 2) { const h = Math.max(24, rh * ov.clientHeight / ov.scrollHeight); th.style.height = `${h}px`; th.style.transform = `translateY(${(rh - h) * top / max}px)`; }
  }
  private controlsTable(): string {
    const dev = (s: string, d: 'k' | 'p') => (s.includes('{') ? s.replace(/\{(k|p):([a-zA-Z0-9]+)\}/g, (_m, _d: string, t: string) => this.part(t, d)) : esc(s));
    const rows = CONTROL_ROWS.map(([a, k, p, t]) => `<tr><th>${esc(a)}</th><td class="ck">${dev(k, 'k')}</td><td class="cp">${dev(p, 'p')}</td><td class="ct">${esc(t)}</td></tr>`).join('');
    return `<div class="ht-ctl"><table><thead><tr><th></th><th class="ck">KEYBOARD</th><th class="cp">CONTROLLER</th><th class="ct">TOUCH</th></tr></thead><tbody>${rows}</tbody></table></div>`
      + `<p class="ht-note">Remap any button or key in Settings › Controls › Button mapping. The highlighted column is the device you are using now.</p>`;
  }

  // ---------------------------------------------------------------- first-launch offer
  showOffer(): void { this.offerOpen = true; this.offerSel = 0; $('#tut-offer').classList.add('show'); this.paintOffer(); }
  private paintOffer(): void { document.querySelectorAll('#tut-offer .btn').forEach((b, k) => b.classList.toggle('focus', k === this.offerSel)); }
  private answerOffer(play: boolean): void { this.offerOpen = false; $('#tut-offer').classList.remove('show'); this.host.click(); this.host.offerAnswered(play); }
  /** title-menu input while the offer is up; true = handled */
  handleOffer(a: UiAction): boolean {
    if (!this.offerOpen) return false;
    if (a === 'left' || a === 'right' || a === 'up' || a === 'down') { this.offerSel = 1 - this.offerSel; this.paintOffer(); this.host.click(); }
    else if (a === 'confirm') this.answerOffer(this.offerSel === 0);
    else if (a === 'back' || a === 'pause') this.answerOffer(false);
    return true;
  }
}
